import React, { useCallback, useId, useMemo, useRef } from "react";
import { ComboOptions, useCombobox } from "@/components/ui/combo-options";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export function normalizeSearchText(value = "") {
  return String(value ?? "").trim().toLowerCase();
}

export function getSearchScore(text = "", search = "") {
  const normalizedText = normalizeSearchText(text);
  const normalizedSearch = normalizeSearchText(search);

  if (!normalizedSearch) return 100;
  if (!normalizedText) return 0;

  const searchTerms = normalizedSearch.split(/\s+/).filter(Boolean);
  let score = 0;

  for (const term of searchTerms) {
    if (!normalizedText.includes(term)) return 0;

    const index = normalizedText.indexOf(term);
    score += 40 - Math.min(index, 20);
    if (normalizedText.startsWith(term)) score += 22;
    if (index === 0) score += 18;
  }

  if (normalizedText.startsWith(normalizedSearch)) score += 25;

  return score;
}

export function getVisibleOptions(options = [], searchTerm = "") {
  const normalizedSearch = normalizeSearchText(searchTerm);

  return [...options]
    .map((option) => {
      const searchableText = [option.label, option.hint, option.value, option.group].filter(Boolean).join(" ");
      return {
        ...option,
        _score: getSearchScore(searchableText, normalizedSearch),
      };
    })
    .filter((option) => (normalizedSearch ? option._score > 0 : true))
    .sort((left, right) => {
      if (right._score !== left._score) return right._score - left._score;
      return String(left.label || "").localeCompare(String(right.label || ""));
    })
    .map(({ _score, ...option }) => option);
}

/**
 * SearchableSelect — combobox-style dropdown with keyword-sensitive filtering.
 *
 * Use this in place of <Select> whenever the option list can grow long
 * (Chart of Accounts, Customers, Products, Salespersons, Branches, etc.).
 *
 * Props
 * -----
 * options       Array<{ value: string, label: string, hint?: string, group?: string }>
 * value         Currently-selected value (string) or "" for none
 * onChange      (value: string) => void
 * placeholder   Placeholder for the trigger + search box (default "Select…")
 * searchPlaceholder  Placeholder shown inside the search field
 * disabled      Disable the trigger
 * clearable     Allow clearing selection (shows an X in the trigger)
 * className     Optional extra class on the trigger
 * emptyMessage  Text shown when search returns no matches
 * testId        data-testid for the trigger button (search input inherits `${testId}-search`, items `${testId}-item-<value>`)
 */
export function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "Select…",
  searchPlaceholder,
  disabled = false,
  clearable = false,
  className,
  emptyMessage = "No matches found.",
  testId,
  ariaLabel,
  required = false,
  grouped = false,
  inputTestId,
}) {
  const anchorRef = useRef(null);
  const selected = options.find((o) => o.value === value);
  const selectedValue = selected?.value;
  const selectedLabel = selectedValue ? selected.label : "";
  const inputId = useId().replace(/:/g, "");
  const listId = `${inputId}-list`;
  const orderedOptions = useMemo(() => getVisibleOptions(options.filter((option) => option.value !== ""), ""), [options]);
  const searchFilter = useCallback((list, text) => getVisibleOptions(list, text), []);

  const combo = useCombobox({
    options: orderedOptions,
    selectedValue,
    selectedLabel,
    onSelect: (next) => onChange && onChange(next),
    onClear: () => onChange && onChange(""),
    filter: searchFilter,
  });

  const handleClear = () => {
    onChange && onChange("");
  };

  return (
    <div className="relative" ref={anchorRef}>
      <input
        id={inputId}
        type="text"
        {...combo.inputProps}
        aria-controls={listId}
        required={required}
        placeholder={combo.open && selectedLabel ? selectedLabel : (searchPlaceholder || `Search ${placeholder.toLowerCase()}`)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        data-testid={inputTestId || (testId ? `${testId}-search` : undefined)}
        data-search-testid={testId ? `${testId}-search` : undefined}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          !selected && "text-muted-foreground",
          className
        )}
      />
      {clearable && selected && !disabled && (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={handleClear}
          className="absolute right-8 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <ComboOptions
        anchorRef={anchorRef}
        open={combo.open && !disabled}
        options={combo.visible.map((option) => ({ ...option, hint: option.hint || (grouped ? option.group : undefined) }))}
        active={combo.active}
        onHover={combo.setActive}
        onPick={combo.pick}
        selectedValue={selectedValue}
        emptyMessage={emptyMessage}
        listId={listId}
        testIdPrefix={testId}
      />
    </div>
  );
}

export default SearchableSelect;
